import React, { useMemo, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DownloadIcon } from 'lucide-react';
import InsightCard from '../components/Dashboard/InsightCard';
import { trendsData } from '../utils/mockData';
const Trends = () => {
  const [timeRange, setTimeRange] = useState('6m');
  // Format currency
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString()}`;
  };
  // Format percentage change
  const calculateChange = (current: number, previous: number) => {
    return previous ? (current - previous) / previous * 100 : 0;
  };
  // Filter data based on selected time range
  const filteredData = useMemo(() => {
    const monthsToShow = timeRange === '3m' ? 3 : timeRange === '6m' ? 6 : 12;
    return {
      sales: trendsData.sales.slice(-monthsToShow),
      cogs: trendsData.cogs.slice(-monthsToShow),
      expenses: trendsData.expenses.slice(-monthsToShow),
      cash: trendsData.cash.slice(-monthsToShow)
    };
  }, [timeRange]);
  return <div data-id="element-2250">
      <div className="mb-6 flex justify-between items-start" data-id="element-2251">
        <div data-id="element-2252">
          <h1 className="text-2xl font-bold text-gray-800" data-id="element-2253">
            Tendencias e Insights
          </h1>
          <p className="text-gray-600" data-id="element-2254">
            Análisis comparativo de tus métricas financieras
          </p>
        </div>
        <button className="flex items-center space-x-2 text-sm bg-blue-600 text-white px-4 py-2.5 rounded-md hover:bg-blue-700 transition-colors" data-id="element-2255">
          <DownloadIcon size={18} data-id="element-2256" />
          <span data-id="element-2257">Exportar PDF</span>
        </button>
      </div>
      {/* Time Range Selector */}
      <div className="flex mb-6" data-id="element-2258">
        <div className="bg-white border border-gray-200 rounded-lg inline-flex" data-id="element-2259">
          <button className={`px-4 py-2 text-sm font-medium rounded-l-lg ${timeRange === '3m' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setTimeRange('3m')} data-id="element-2260">
            3 meses
          </button>
          <button className={`px-4 py-2 text-sm font-medium ${timeRange === '6m' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setTimeRange('6m')} data-id="element-2261">
            6 meses
          </button>
          <button className={`px-4 py-2 text-sm font-medium rounded-r-lg ${timeRange === '12m' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} onClick={() => setTimeRange('12m')} data-id="element-2262">
            12 meses
          </button>
        </div>
      </div>
      {/* Main Metrics Trends */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2263">
        {/* Sales Trend */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-2264">
          <h2 className="text-lg font-semibold mb-4" data-id="element-2265">Tendencia de Ventas</h2>
          <div className="h-72" data-id="element-2266">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2267">
              <LineChart data={filteredData.sales} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-2268">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2269" />
                <XAxis dataKey="month" data-id="element-2270" />
                <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-2271" />
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-2272" />
                <Legend data-id="element-2273" />
                <Line type="monotone" dataKey="value" name="Ventas" stroke="#4F46E5" strokeWidth={2} data-id="element-2274" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* COGS Trend */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-2275">
          <h2 className="text-lg font-semibold mb-4" data-id="element-2276">
            Tendencia de Costo de Venta
          </h2>
          <div className="h-72" data-id="element-2277">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2278">
              <LineChart data={filteredData.cogs} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-2279">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2280" />
                <XAxis dataKey="month" data-id="element-2281" />
                <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-2282" />
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-2283" />
                <Legend data-id="element-2284" />
                <Line type="monotone" dataKey="value" name="Costo de Venta" stroke="#F97316" strokeWidth={2} data-id="element-2285" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Expenses Trend */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-2286">
          <h2 className="text-lg font-semibold mb-4" data-id="element-2287">Tendencia de Gastos</h2>
          <div className="h-72" data-id="element-2288">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2289">
              <LineChart data={filteredData.expenses} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-2290">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2291" />
                <XAxis dataKey="month" data-id="element-2292" />
                <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-2293" />
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-2294" />
                <Legend data-id="element-2295" />
                <Line type="monotone" dataKey="value" name="Gastos" stroke="#EC4899" strokeWidth={2} data-id="element-2296" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Cash Trend */}
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-2297">
          <h2 className="text-lg font-semibold mb-4" data-id="element-2298">Tendencia de Cash</h2>
          <div className="h-72" data-id="element-2299">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2300">
              <LineChart data={filteredData.cash} margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-2301">
                <CartesianGrid strokeDasharray="3 3" data-id="element-2302" />
                <XAxis dataKey="month" data-id="element-2303" />
                <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-2304" />
                <Tooltip formatter={value => [formatCurrency(value as number), undefined]} labelFormatter={label => `Mes: ${label}`} data-id="element-2305" />
                <Legend data-id="element-2306" />
                <Line type="monotone" dataKey="value" name="Cash" stroke="#10B981" strokeWidth={2} data-id="element-2307" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {/* Comparative Analysis */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-2308">
        <h2 className="text-lg font-semibold mb-4" data-id="element-2309">Análisis Comparativo</h2>
        <div className="overflow-x-auto" data-id="element-2310">
          <table className="w-full" data-id="element-2311">
            <thead data-id="element-2312">
              <tr className="bg-gray-50" data-id="element-2313">
                <th className="py-3 px-4 text-left text-sm font-medium text-gray-500" data-id="element-2314">
                  Métrica
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-2315">
                  Actual
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-2316">
                  Mes Anterior
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-2317">
                  Var. Mensual
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-2318">
                  Año Anterior
                </th>
                <th className="py-3 px-4 text-right text-sm font-medium text-gray-500" data-id="element-2319">
                  Var. Anual
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-2320">
              {trendsData.comparisons.map(item => {
              const monthlyChange = calculateChange(item.actual, item.prevMonth);
              const yearlyChange = calculateChange(item.actual, item.prevYear);
              return <tr key={item.metric} data-id="element-2321">
                    <td className="py-3 px-4 text-sm font-medium" data-id="element-2322">
                      {item.metric}
                    </td>
                    <td className="py-3 px-4 text-right text-sm" data-id="element-2323">
                      {formatCurrency(item.actual)}
                    </td>
                    <td className="py-3 px-4 text-right text-sm" data-id="element-2324">
                      {formatCurrency(item.prevMonth)}
                    </td>
                    <td className={`py-3 px-4 text-right text-sm ${monthlyChange >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-2325">
                      {monthlyChange >= 0 ? '+' : ''}
                      {monthlyChange.toFixed(1)}%
                    </td>
                    <td className="py-3 px-4 text-right text-sm" data-id="element-2326">
                      {formatCurrency(item.prevYear)}
                    </td>
                    <td className={`py-3 px-4 text-right text-sm ${yearlyChange >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-2327">
                      {yearlyChange >= 0 ? '+' : ''}
                      {yearlyChange.toFixed(1)}%
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Comparative Chart */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6" data-id="element-2328">
        <h2 className="text-lg font-semibold mb-4" data-id="element-2329">
          Comparación Mes Actual vs Anterior
        </h2>
        <div className="h-80" data-id="element-2330">
          <ResponsiveContainer width="100%" height="100%" data-id="element-2331">
            <BarChart data={trendsData.comparisons} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-2332">
              <CartesianGrid strokeDasharray="3 3" data-id="element-2333" />
              <XAxis dataKey="metric" data-id="element-2334" />
              <YAxis tickFormatter={value => `$${(value / 1000).toFixed(0)}K`} data-id="element-2335" />
              <Tooltip formatter={value => [formatCurrency(value as number), undefined]} data-id="element-2336" />
              <Legend data-id="element-2337" />
              <Bar dataKey="actual" name="Mes Actual" fill="#4F46E5" radius={[4, 4, 0, 0]} data-id="element-2338" />
              <Bar dataKey="prevMonth" name="Mes Anterior" fill="#94A3B8" radius={[4, 4, 0, 0]} data-id="element-2339" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Insights Section */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-2340">
        <h2 className="text-lg font-semibold mb-4" data-id="element-2341">Insights Automáticos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2342">
          <InsightCard type="positive" message="Tus ventas crecieron 18.5% este mes y 45.7% vs el mismo mes del año pasado." data-id="element-2343" />
          <InsightCard type="warning" message="Estás gastando 12% más en nómina que el promedio de los últimos 6 meses." data-id="element-2344" />
          <InsightCard type="negative" message="Tu cash disponible cayó 31.9% desde su máximo hace 2 meses. Revisa tu flujo de efectivo." data-id="element-2345" />
          <InsightCard type="info" message="Tu margen bruto actual (45.2%) está 0.7 puntos porcentuales por debajo del promedio semestral." data-id="element-2346" />
          <InsightCard type="warning" message="Inventario demasiado alto: +45% sobre promedio histórico. Considera estrategias para reducirlo." data-id="element-2347" />
          <InsightCard type="positive" message="Tu utilidad neta creció 11.5% vs mes anterior a pesar del aumento en costos." data-id="element-2348" />
        </div>
      </div>
    </div>;
};
export default Trends;